import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Ein Strichcode von der Packung: 8 bis 14 Ziffern (EAN-8, EAN-13, UPC). */
export function isFoodBarcode(text: string): boolean {
  return /^\d{8,14}$/.test(text);
}

type Problem = 'denied' | 'noCamera' | 'failed';

/** So lange ohne erkannten Code, dann sagt die Ansicht, was sonst geht. */
const SLOW_MS = 15_000;

/**
 * Strichcode mit der Kamera lesen. Die Erkennung (ZXing) wird erst hier
 * nachgeladen — wer nie scannt, lädt sie nie. Nur Lebensmittel-Codes
 * (EAN/UPC): Das macht die Erkennung schneller und QR-Codes auf der Packung
 * lösen nichts aus.
 *
 * Die Kamera ist nur an, solange diese Ansicht steht; danach wird jede Spur
 * gestoppt — ein grünes Kamera-Licht nach dem Scannen wäre ein Vertrauensbruch.
 */
export function BarcodeScanner({
  onCode,
  onClose,
}: {
  onCode: (code: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  // Der technische Grund, klein dazu — damit ein Fehler am Gerät messbar ist,
  // statt nur „ging nicht“ (21.10.2026: Scannen am iPhone ging nicht).
  const [detail, setDetail] = useState<string | null>(null);
  const [slow, setSlow] = useState(false);
  const done = useRef(onCode);
  done.current = onCode;

  useEffect(() => {
    let stop: (() => void) | null = null;
    let cancelled = false;
    const timer = setTimeout(() => setSlow(true), SLOW_MS);

    void (async () => {
      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all(
          [import('@zxing/browser'), import('@zxing/library')],
        );
        if (cancelled || !video.current) return;
        const hints = new Map([
          [
            DecodeHintType.POSSIBLE_FORMATS,
            [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E],
          ],
        ]);
        const reader = new BrowserMultiFormatReader(hints);
        // Der erste Treffer kann kommen, bevor `decodeFromConstraints` die
        // Steuerung zurückgibt — liegt der Code schon im Bild, sofort. Ein
        // `controls.stop()` im Rückruf warf dann (die Konstante gab es noch
        // nicht), ZXing brach ab, und der Code kam nie an: schwarzes Bild,
        // „Noch kein Code erkannt“ (Geräteabnahme 28.10.2026). Deshalb nur
        // merken; gestoppt wird, sobald die Steuerung da ist.
        let found = false;
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } }, audio: false },
          video.current,
          (result) => {
            const text = result?.getText();
            if (found || !text || !isFoodBarcode(text)) return;
            found = true;
            stop?.();
            done.current(text);
          },
        );
        if (cancelled || found) controls.stop();
        else stop = () => controls.stop();
      } catch (error) {
        if (cancelled) return;
        const name = (error as { name?: string } | null)?.name;
        const message = (error as { message?: string } | null)?.message;
        setDetail([name, message].filter(Boolean).join(': ').slice(0, 160) || null);
        setProblem(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'denied'
            : name === 'NotFoundError' || name === 'OverconstrainedError'
              ? 'noCamera'
              : 'failed',
        );
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      stop?.();
    };
  }, []);

  return (
    <div className="flex flex-col gap-2">
      {problem ? (
        <div role="alert" className="flex flex-col gap-1">
          <p className="text-sm text-danger">{t(`scan.problem.${problem}`)}</p>
          {detail ? <p className="text-xs break-words text-ink-muted">{detail}</p> : null}
        </div>
      ) : (
        <>
          {/* Stummes Vorschaubild — `playsInline`, sonst springt iOS in den Vollbild-Player. */}
          <video
            ref={video}
            muted
            playsInline
            aria-label={t('scan.preview')}
            className="aspect-[4/3] w-full rounded-xl bg-black object-cover"
          />
          <p className="text-xs text-ink-muted">{t('scan.hint')}</p>
          {slow ? (
            <p role="status" className="text-sm text-warn">
              {t('scan.slow')}
            </p>
          ) : null}
        </>
      )}
      <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet kw-btn-small">
        {t('scan.close')}
      </button>
    </div>
  );
}
